import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { environment } from '@lohi/environments/environment';
import { sort } from 'remeda';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { AuthService } from 'src/app/root/auth.service';
import { MetricCards, SettlementStatementSummary, StatementsOverview } from 'src/app/types';
import {
  MigrationMappableTarget,
  MigrationMapService,
} from '../../owner-dashboard/owner-dashboard/migration-map/migration-map.service';
import { CompanyService, KPIDuration } from '../../root/company.service';
import { PaymentIssueDialogComponent } from '../../shared/payment-issue-dialog/payment-issue-dialog.component';
import { SnackBarService } from '../../shared/snackbar.service';
import { StatementService } from '../statement.service';

@Component({
  selector: 'lohi-settlement-list',
  templateUrl: './settlement-list.component.html',
  styles: [
    `
      .slashed-background {
        background-color: #0fb0f0;
        background-image: linear-gradient(70deg, #1794c6 50%, #0fb0f0 50%);
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SettlementListComponent implements OnInit {
  constructor(
    private statementService: StatementService,
    private authService: AuthService,
    private snackbarService: SnackBarService,
    private migrationMapService: MigrationMapService,
    private companyService: CompanyService,
    private dialog: MatDialog,
  ) {
    this.metricCards$ = this.companyService.revenueData$.pipe(map((data) => data?.metricCards));
  }
  public summaries$: Observable<SettlementStatementSummary[]>;
  public openedSummaryId$$ = new BehaviorSubject<number>(null);
  public overview$: Observable<StatementsOverview>;
  public inviteDriverUrl$: Observable<string>;
  public randomNumber = Math.floor(Math.random() * 10) + 1; // returns a random integer from 0 to 9
  public targets$: Observable<MigrationMappableTarget[]>;
  public maxProfit = 0;
  public metricCards$: Observable<MetricCards>;

  protected readonly KPIDuration = KPIDuration;

  public ngOnInit(): void {
    this.targets$ = this.migrationMapService.migrationMapTargets$;
    this.getMaxProfit();
    this.overview$ = this.statementService.summaries$;
    this.summaries$ = this.overview$.pipe(
      map((summaries) => sort(summaries?.statements || [], (a, b) => b.date.localeCompare(a.date))),
    );
    this.inviteDriverUrl$ = this.authService.userInfo$.pipe(
      map(
        (user) =>
          `${environment.production ? 'https://status.lohi.ai/status?c=' : 'https://stage.api.5f.app/status?c='}${
            user.companyId
          }`,
      ),
    );
  }

  public async getFuelTransactions() {
    try {
      const data = await this.statementService.getTransactions$().pipe(take(1)).toPromise();

      const blob = new Blob([data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = new Date() + '.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      this.snackbarService.showError('No transactions to download');
    }
  }

  public getMaxProfit() {
    this.targets$.pipe(take(2)).subscribe((targets) => {
      for (const target of targets) {
        if (target.maxShiftCentsDifference > this.maxProfit) {
          this.maxProfit = target.maxShiftCentsDifference;
        }
      }
    });
  }

  public openReportIssue(event: MouseEvent, summary: SettlementStatementSummary) {
    event.preventDefault();
    event.stopPropagation();
    const ref = this.dialog.open(PaymentIssueDialogComponent, {
      width: '600px',
      data: {
        mode: 'report',
        context: 'settlement',
        settlementId: summary.id,
        link: { routerLink: ['/settlement', 'details', summary.id], text: String(summary.id) },
      },
    });
    ref.afterClosed().subscribe((result) => {
      if (result?.submitted) {
        this.snackbarService.show('Payment issue submitted');
      }
    });
  }

  public openViewIssue(event: MouseEvent, summary: SettlementStatementSummary) {
    event.preventDefault();
    event.stopPropagation();
    const resolved = !!summary.paymentIssue?.respondedAt;
    this.dialog.open(PaymentIssueDialogComponent, {
      width: '600px',
      data: {
        mode: resolved ? 'resolved' : 'submitted',
        context: 'settlement',
        settlementId: summary.id,
        paymentIssue: summary.paymentIssue,
        link: { routerLink: ['/settlement', 'details', summary.id], text: String(summary.id) },
      },
    });
  }
}
